/**
 * Formatting Utilities
 *
 * Shared formatting functions for displaying market data.
 */

/**
 * Format volume number to human-readable currency string.
 * @example formatVolume(23708083) => "$23,708,083"
 */
export function formatVolume(volume: number): string {
  return `$${volume.toLocaleString("en-US")}`;
}

/**
 * Compact currency formatter for the card outcome rows.
 * @example formatVolumeShort(235000)   => "$235k"
 * @example formatVolumeShort(1500000)  => "$1.5M"
 * @example formatVolumeShort(7000000)  => "$7M"
 */
export function formatVolumeShort(amount: number): string {
  if (amount >= 1_000_000) {
    const millions = (amount / 1_000_000).toFixed(1).replace(/\.0$/, "");
    return `$${millions}M`;
  }
  if (amount >= 1_000) {
    return `$${Math.round(amount / 1_000)}k`;
  }
  return `$${amount}`;
}

/**
 * Format price to currency string with 2 decimal places.
 * @example formatPrice(0.72) => "$0.72"
 */
export function formatPrice(price: number): string {
  return `$${price.toFixed(2)}`;
}

export const PriceChangeDirection = {
  Up: "up",
  Down: "down",
  Neutral: "neutral",
} as const;
export type PriceChangeDirection =
  (typeof PriceChangeDirection)[keyof typeof PriceChangeDirection];

/**
 * 24h price change formatted for the card outcome rows. Returns the
 * absolute-value as bare digits (no `%` symbol — the i18n template owns
 * that so it can localize) plus a direction tag the caller uses to pick
 * the trending up/down icon.
 * @example formatPriceChange(6.2)   => { text: "6.2",  direction: Up }
 * @example formatPriceChange(-3.4)  => { text: "3.4",  direction: Down }
 * @example formatPriceChange(0)     => { text: "0.0",  direction: Neutral }
 */
export function formatPriceChange(percent: number): {
  text: string;
  direction: PriceChangeDirection;
} {
  const direction =
    percent > 0
      ? PriceChangeDirection.Up
      : percent < 0
        ? PriceChangeDirection.Down
        : PriceChangeDirection.Neutral;
  return { text: Math.abs(percent).toFixed(1), direction };
}

/**
 * Time-left value for the card surface. Returns either a day count (>= 24h
 * to expiry) or an HH:MM:SS string (< 24h). Returns null if expired or
 * missing — callers gate the time-left pill on null.
 *
 * The day-count `kind` is rendered through i18n by the caller (singular vs
 * plural plurals vary by locale); the HH:MM:SS `kind` is locale-neutral.
 */
export type TimeLeft =
  | { kind: "days"; count: number }
  | { kind: "hms"; text: string };

export function formatTimeLeftValue(
  expiresAt: string | undefined,
): TimeLeft | null {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return null;

  const oneDay = 24 * 60 * 60 * 1000;
  const days = Math.floor(diff / oneDay);
  if (days >= 1) return { kind: "days", count: days };

  const pad = (n: number): string => n.toString().padStart(2, "0");
  const hours = Math.floor(diff / (60 * 60 * 1000));
  const minutes = Math.floor((diff % (60 * 60 * 1000)) / (60 * 1000));
  const seconds = Math.floor((diff % (60 * 1000)) / 1000);
  return { kind: "hms", text: `${pad(hours)}:${pad(minutes)}:${pad(seconds)}` };
}

/**
 * Normalizes whitespace in text by replacing multiple spaces/newlines
 * with a single space and trimming.
 */
export function normalizeWhitespace(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}
